import produce from "immer";
import React, { useCallback, useEffect, useState } from "react";
import { UseMutationResult } from "react-query";
import EnabledDraggableSkuRule from "./EnabledDraggableSkuRule";
import { Response, SkuContentRule, Skus } from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";

type OrderableSkuRulesProps = {
  sku: Skus;
  skuRules: SkuContentRule[];
  expandedSkuRuleIds: string[];
  toggleExpansion: (skuRuleId: string) => void;
  readonly?: boolean;
  cloneSkuRule: (skuRuleId: string) => Promise<void>;
  isMembershipGatedAccessEnabled: boolean;
  deleteSkuRuleMutation: UseMutationResult<
    Response,
    unknown,
    { skuRuleId: string },
    unknown
  >;
  reorderSkuRulesMutation: UseMutationResult<
    void,
    unknown,
    SkuContentRule[],
    unknown
  >;
} & SkuFormProps;

const OrderableSkuRules = ({
  sku,
  skuRules,
  cloneSkuRule,
  deleteSkuRuleMutation,
  reorderSkuRulesMutation,
  expandedSkuRuleIds,
  toggleExpansion,
  readonly = false,
  isMembershipGatedAccessEnabled,
  onDirty,
  onClean,
  onSkuUpdated,
}: OrderableSkuRulesProps) => {
  const [orderedSkuRules, setOrderedSkuRules] = useState(skuRules);
  const deleteSkuRule = useCallback(
    (skuRuleId: string) => deleteSkuRuleMutation.mutate({ skuRuleId }),
    [deleteSkuRuleMutation],
  );

  const onMoveSkuRule = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragSkuRule = orderedSkuRules[dragIndex];
      const updatedSkuRuleOrder = produce(orderedSkuRules!, (draft) => {
        draft.splice(dragIndex, 1);
        draft.splice(hoverIndex, 0, dragSkuRule);
      });
      setOrderedSkuRules(updatedSkuRuleOrder);
    },
    [orderedSkuRules],
  );
  const onDropSkuRule = useCallback(
    () => reorderSkuRulesMutation.mutate(orderedSkuRules),
    [orderedSkuRules, reorderSkuRulesMutation],
  );

  useEffect(() => {
    setOrderedSkuRules(skuRules);
  }, [skuRules]);

  return (
    <div>
      {orderedSkuRules.map((skuRule, i) => {
        return (
          <EnabledDraggableSkuRule
            key={skuRule.id}
            skuRule={skuRule}
            sku={sku}
            index={i}
            expanded={expandedSkuRuleIds.includes(skuRule.id)}
            toggleExpansion={() => toggleExpansion(skuRule.id)}
            cloneSkuRule={cloneSkuRule}
            deleteSkuRule={deleteSkuRule}
            isMembershipGatedAccessEnabled={isMembershipGatedAccessEnabled}
            onMoveSkuRule={onMoveSkuRule}
            onDropSkuRule={onDropSkuRule}
            readonly={readonly}
            onDirty={onDirty}
            onClean={onClean}
            onSkuUpdated={onSkuUpdated}
          />
        );
      })}
    </div>
  );
};

export default OrderableSkuRules;
